package fr.manyverse.app.presentation.components.player_screen.inventory

import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.InventoryItem
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.util.normalizeForSearch
import fr.manyverse.app.presentation.components.inventory.label
import fr.manyverse.app.presentation.viewmodel.InventoryViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.inventory_delete_item
import manyverse.shared.generated.resources.inventory_delete_item_confirm
import manyverse.shared.generated.resources.inventory_no_items
import org.jetbrains.compose.resources.stringResource

@Composable
fun InventoryItemList(
    items: List<InventoryItem>,
    selectedCategories: Set<ItemCategory>,
    searchQuery: String,
    pendingQuantities: Map<String, Int>,
    viewModel: InventoryViewModel,
    onGiveClick: (InventoryItem) -> Unit,
    showGiveButton: Boolean = true,
    modifier: Modifier = Modifier
) {
    val filtered = items.filter { item ->
        item.category in selectedCategories &&
                (searchQuery.isBlank() || item.name.normalizeForSearch().contains(searchQuery.normalizeForSearch()))
    }

    var pendingDelete by remember { mutableStateOf<InventoryItem?>(null) }

    if (filtered.isEmpty()) {
        Text(
            text = stringResource(Res.string.inventory_no_items),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = modifier.padding(24.dp)
        )
    } else {
        LazyVerticalGrid(
            columns = GridCells.Adaptive(minSize = 120.dp),
            modifier = modifier.fillMaxSize(),
            contentPadding = PaddingValues(8.dp),
        ) {
            if (selectedCategories.size != 1) {
                ItemCategory.entries.forEach { category ->
                    val categoryItems = filtered.filter { it.category == category }
                    if (categoryItems.isNotEmpty()) {
                        item(key = "header_${category.name}", span = { GridItemSpan(maxLineSpan) }) {
                            Text(
                                text = category.label(),
                                style = MaterialTheme.typography.titleSmall,
                                fontWeight = FontWeight.SemiBold,
                                color = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.padding(
                                    start = 8.dp,
                                    end = 8.dp,
                                    top = 12.dp,
                                    bottom = 4.dp
                                )
                            )
                        }
                        items(categoryItems, key = { it.id }) { item ->
                            InventoryItemCard(
                                item = item,
                                displayQuantity = pendingQuantities[item.id] ?: item.quantity,
                                viewModel = viewModel,
                                onDeleteClick = { pendingDelete = item },
                                onGiveClick = { onGiveClick(item) },
                                showGiveButton = showGiveButton
                            )
                        }
                    }
                }
            } else {
                items(filtered, key = { it.id }) { item ->
                    InventoryItemCard(
                        item = item,
                        displayQuantity = pendingQuantities[item.id] ?: item.quantity,
                        viewModel = viewModel,
                        onDeleteClick = { pendingDelete = item },
                        onGiveClick = { onGiveClick(item) },
                        showGiveButton = showGiveButton
                    )
                }
            }
        }
    }

    pendingDelete?.let { item ->
        AlertDialog(
            onDismissRequest = { pendingDelete = null },
            title = { Text(stringResource(Res.string.inventory_delete_item)) },
            text = { Text(stringResource(Res.string.inventory_delete_item_confirm)) },
            confirmButton = {
                TextButton(onClick = {
                    viewModel.deleteItem(item)
                    pendingDelete = null
                }) {
                    Text(stringResource(Res.string.inventory_delete_item))
                }
            },
            dismissButton = {
                TextButton(onClick = { pendingDelete = null }) {
                    Text(stringResource(Res.string.cancel))
                }
            }
        )
    }
}

